import quantize from 'quantize';
import { gray7 } from '@spotify-internal/encore-foundation';

/* similar to https://ghe.spotify.net/web-platform/open/blob/master/web-player/src/middlewares/uiMiddleware.js */
export function extractVibrantColor(palette) {
  let maxDiff = 0.0;
  let bestMatch;
  palette.forEach(color => {
    const diff =
      Math.pow(color[0] - color[1], 2) +
      Math.pow(color[1] - color[2], 2) +
      Math.pow(color[0] - color[2], 2);
    if (diff > maxDiff) {
      bestMatch = color;
      maxDiff = diff;
    }
  });
  return bestMatch;
}

/* similar to https://ghe.spotify.net/web-platform/open/blob/master/web-player/src/middlewares/uiMiddleware.js */
export function fetchImage(imageUrl) {
  return new Promise((resolve, reject) => {
    if (!imageUrl) reject('No image');
    const domImage = new Image();
    domImage.crossOrigin = 'Anonymous';
    domImage.onload = () => resolve(domImage);
    domImage.onerror = e => {
      reject(e);
    };
    domImage.src = imageUrl;
  });
}

/* similar to https://ghe.spotify.net/web-platform/open/blob/master/web-player/src/middlewares/uiMiddleware.js */
export function getImagePalette(image, quality = 10) {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  const pixelCount = width * height;
  const pixelArray = [];

  for (let i = 0, offset, r, g, b, a; i < pixelCount; i += quality) {
    offset = i * 4;
    r = pixels[offset + 0];
    g = pixels[offset + 1];
    b = pixels[offset + 2];
    a = pixels[offset + 3];

    // If pixel is mostly opaque and not white
    if (a >= 125) {
      if (!(r > 250 && g > 250 && b > 250)) {
        pixelArray.push([r, g, b]);
      }
    }
  }

  const cmap = quantize(pixelArray, 5);
  return cmap.palette();
}

/* similar to http://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion */
export function rgbToHsl(r, g, b) {
  const r2 = r / 255;
  const g2 = g / 255;
  const b2 = b / 255;
  const max = Math.max(r2, g2, b2);
  const min = Math.min(r2, g2, b2);
  let h;
  let s;
  const l = (max + min) / 2;

  if (max === min) {
    h = 0;
    s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    /* istanbul ignore next */
    switch (max) {
      /* istanbul ignore next */
      case r2:
        h = (g2 - b2) / d + (g2 < b2 ? 6 : 0);
        break;
      /* istanbul ignore next */
      case g2:
        h = (b2 - r2) / d + 2;
        break;
      /* istanbul ignore next */
      case b2:
        h = (r2 - g2) / d + 4;
        break;
      /* istanbul ignore next */
      default:
        break;
    }
    h /= 6;
  }
  return [h, s, l];
}

/**
 * Get Linear Gradient for image
 * @param imagePath
 * @param percent
 * @returns {Promise<any>}
 */
export async function getGradient(imagePath, percent = '85') {
  return new Promise(async (resolve, reject) => {
    try {
      let palette;
      try {
        const domImage = await fetchImage(imagePath);
        palette = await getImagePalette(domImage);
      } catch (e) {
        // A smattering of grays
        palette = [
          [217, 218, 220],
          [0, 0, 0],
          [255, 255, 255],
          [117, 117, 117],
          [158, 158, 158],
        ];
      }
      const [r, g, b] = await extractVibrantColor(palette);
      const [h, s, l] = rgbToHsl(r, g, b);
      resolve({
        backgroundGradient: `linear-gradient(
          to bottom,
          hsl(${360 * h}, ${100 * s}%,
          ${Math.min(100 * l, 30)}%),
          ${gray7} ${percent}%
        )`,
        palette,
      });
    } catch (error) {
      reject(error);
    }
  });
}
